import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useShopVertical } from "../../lib/useShopVertical";
import { Sparkles, Loader2, Trash2, Wand2, RefreshCw, Download, Upload, Copy, Check } from "lucide-react";
import { apiClient } from "../../lib/api";
import type { Analysis, AnalysisDesign, Design } from "@erc/shared";
import { DesignDetailModal } from "../../pages/DesignsPage";
import { buildBasePrompt, buildFullImagePrompt, FIDELITY_LABELS, fidelityOptionsFor, isFidelity, useFidelity, type Fidelity } from "../../lib/designPrompt";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";

const API_BASE = import.meta.env.VITE_API_URL || "";
const imgUrl = (u: string | null) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

export function DesignWorkshop({ analysis, presetPrompt, presetNonce }: { analysis: Analysis; presetPrompt?: string; presetNonce?: number }) {
  const queryClient = useQueryClient();
  const rootRef = useRef<HTMLElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [prompt, setPrompt] = useState(() => buildBasePrompt(analysis));
  // IP sIkI modlu magazada Yakin/Benzer yok
  const ipStrict = !!useShopVertical().shop?.ipStrict;
  const [fidelity, setFidelity] = useFidelity(ipStrict);
  const FIDELITY_OPTIONS = fidelityOptionsFor(ipStrict);
  // Her ekran KENDI modelini hatirlar: atolyede kullanilan model, toplu
  // uretimde ya da mockup modalinda istenen model olmayabiliyor.
  const { models, model, setModel } = useDesignModel("erc:model:design-workshop");
  const [revising, setRevising] = useState<string | null>(null);
  const [revisePrompt, setRevisePrompt] = useState("");
  const [copiedFid, setCopiedFid] = useState<Fidelity | null>(null);
  const [copiedDesignId, setCopiedDesignId] = useState<string | null>(null);

  // Referans (kaynak urun) gorseli — close/balanced kademesinde bu goruntu baz alinir
  const referenceImageUrl = imgUrl(analysis.source?.imageUrls?.[0] ?? null) || undefined;

  const copyPromptFor = (fid: Fidelity) => {
    navigator.clipboard.writeText(buildFullImagePrompt(prompt, fid, referenceImageUrl)).then(() => {
      setCopiedFid(fid);
      setTimeout(() => setCopiedFid(null), 1500);
    }).catch(() => { /* ignore */ });
  };

  // Uretilmis bir tasarimin KENDI prompt'unu (kademesiyle) kopyala
  const copyDesignPrompt = (d: AnalysisDesign) => {
    const fid: Fidelity = isFidelity(d.fidelity) ? d.fidelity : "original";
    navigator.clipboard.writeText(buildFullImagePrompt(d.prompt || prompt, fid, referenceImageUrl)).then(() => {
      setCopiedDesignId(d.id);
      setTimeout(() => setCopiedDesignId(null), 1500);
    }).catch(() => { /* ignore */ });
  };

  // Brief'ten gelen preset prompt: textarea'yı doldur + atölyeye kaydır
  useEffect(() => {
    if (presetNonce && presetPrompt) {
      setPrompt(presetPrompt);
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [presetNonce, presetPrompt]);

  // Tasarima tiklayinca Tasarimlar sayfasindaki duzenleme modali (havuzdaki kaydi)
  const [detail, setDetail] = useState<Design | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const openDetail = async (analysisDesignId: string) => {
    setDetailError(null);
    try {
      const map = await apiClient.getAnalysisDesignPoolMap(analysis.id);
      const poolId = map[analysisDesignId]?.designId;
      if (!poolId) { setDetailError("Tasarım havuzda bulunamadı (henüz kaydedilmemiş olabilir)."); return; }
      setDetail(await apiClient.getDesign(poolId));
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : String(e));
    }
  };
  const { data: designs = [] } = useQuery({
    queryKey: ["analysis-designs", analysis.id],
    queryFn: () => apiClient.listAnalysisDesigns(analysis.id),
    refetchInterval: (query) => {
      const data = query.state.data as AnalysisDesign[] | undefined;
      return data?.some((d) => d.status === "generating") ? 3000 : false;
    },
  });

  // Arka plan temizligi: kapaliyken model ciktisi oldugu gibi kaydedilir; son secim hatirlanir
  const [cleanBg, setCleanBg] = useState<boolean>(() => {
    try { return localStorage.getItem("erc:workshop:clean-bg") !== "0"; } catch { return true; }
  });
  const toggleCleanBg = (v: boolean) => {
    setCleanBg(v);
    try { localStorage.setItem("erc:workshop:clean-bg", v ? "1" : "0"); } catch { /* yoksay */ }
  };
  // Baski alani: ornament analizinde varsayilan daire; tasarim bu sekle gore kurulur
  const [printShape, setPrintShape] = useState<string>(() => (analysis.productTypeKey === "ornament" ? "round" : "tshirt"));
  const genM = useMutation({
    mutationFn: (payload: { prompt: string; model: string; fidelity?: string; referenceDesignId?: string }) =>
      apiClient.generateAnalysisDesign(analysis.id, { ...payload, cleanBackground: cleanBg, printShape }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analysis-designs", analysis.id] });
      setRevising(null);
      setRevisePrompt("");
    },
  });

  const delM = useMutation({
    mutationFn: (designId: string) => apiClient.deleteAnalysisDesign(analysis.id, designId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["analysis-designs", analysis.id] }),
  });

  const uploadM = useMutation({
    mutationFn: (dataUrl: string) => apiClient.uploadAnalysisDesign(analysis.id, dataUrl),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["analysis-designs", analysis.id] }),
  });

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { if (typeof reader.result === "string") uploadM.mutate(reader.result); };
    reader.readAsDataURL(file);
  }

  const canGen = prompt.trim() && model && !genM.isPending;

  return (
    <section ref={rootRef} className="scroll-mt-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Wand2 className="h-4 w-4 text-fuchsia-500" />
        <h3 className="text-base font-semibold text-gray-900">Tasarım Atölyesi</h3>
        <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-medium text-fuchsia-600">AI ile üret</span>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploadM.isPending}
          className="ml-auto flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {uploadM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Tasarım Yükle
        </button>
      </div>

      {/* İlham kademesi */}
      <div className="mb-3">
        <p className="mb-1.5 text-xs font-medium text-gray-500">İlham kademesi</p>
        <div className="flex flex-wrap gap-2">
          {FIDELITY_OPTIONS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFidelity(f.key)}
              title={f.hint}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                fidelity === f.key
                  ? "border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="self-center text-[11px] text-gray-400">
            {FIDELITY_OPTIONS.find((f) => f.key === fidelity)?.hint}
          </span>
        </div>
      </div>

      {/* Prompt */}
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500">Tasarım prompt'u (düzenleyebilir / kendi ekleyebilirsin)</p>
          <button onClick={() => setPrompt(buildBasePrompt(analysis))} className="text-[11px] text-indigo-600 hover:text-indigo-700">
            Analizden yeniden doldur
          </button>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          className="w-full resize-y rounded-lg border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-200"
        />
        {/* Tam prompt'u kademeye gore kopyala — baska bir araçta görsel üretmek için */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-gray-400">Prompt kopyala:</span>
          {(Object.keys(FIDELITY_LABELS) as Fidelity[]).map((fid) => (
            <button
              key={fid}
              onClick={() => copyPromptFor(fid)}
              disabled={!prompt.trim()}
              title={`"${FIDELITY_LABELS[fid]}" kademesinin tam prompt'unu kopyala`}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              {copiedFid === fid ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
              {FIDELITY_LABELS[fid]}
            </button>
          ))}
        </div>
      </div>

      {/* Model + üret */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-200"
        >
          {models.length === 0 && <option value="">Model yükleniyor…</option>}
          {models.map((m) => (
            <option key={m.id} value={m.id}>{modelLabel(m)}</option>
          ))}
        </select>
        <select value={printShape} onChange={(e) => setPrintShape(e.target.value)}
          aria-label="Baskı alanı" title="Tasarım bu baskı alanına göre kurulur: ornament için şeklin içine ortalanır, üstte asma deliği payı kalır"
          className="rounded-lg border border-gray-300 px-2 py-2 text-sm">
          <option value="tshirt">Tişört (7:8)</option>
          <option value="round">Ornament: yuvarlak</option>
          <option value="heart">Ornament: kalp</option>
          <option value="square">Ornament: kare</option>
        </select>
        <button
          onClick={() => genM.mutate({ prompt, model, fidelity })}
          disabled={!canGen}
          className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
        >
          {genM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Tasarım Üret
        </button>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600"
          title="Açıkken modelin yeşil/düz zemini silinir ve kalıntılar temizlenir. Kapalıyken model çıktısı olduğu gibi (zemin dahil) kaydedilir — hataların modelden mi geldiğini görmek için.">
          <input type="checkbox" checked={cleanBg} onChange={(e) => toggleCleanBg(e.target.checked)} className="h-3.5 w-3.5 accent-fuchsia-600" />
          Arka planı temizle
        </label>
        {genM.isError && <span className="text-xs text-red-500">{(genM.error as Error)?.message ?? "Hata"}</span>}
      </div>

      {detailError && <p className="mb-2 text-xs text-red-500">{detailError}</p>}
      {/* Galeri */}
      {designs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-xs text-gray-400">
          Henüz tasarım üretilmedi. Yukarıdan prompt'u ayarla, kademeyi seç ve "Tasarım Üret"e bas.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {designs.map((d) => (
            <div key={d.id} className="group relative overflow-hidden rounded-lg border border-gray-200 bg-alpha-check">
              <div className="aspect-square w-full">
                {d.status === "completed" && d.imageUrl ? (
                  <button type="button" onClick={() => void openDetail(d.id)} title="Tıkla: tasarımı düzenle (varyant, Canva, arka plan…)"
                    className="block h-full w-full cursor-pointer">
                    <img src={imgUrl(d.imageUrl)} alt="" className="h-full w-full object-contain" />
                  </button>
                ) : d.status === "failed" ? (
                  <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center">
                    <span className="text-xs font-medium text-red-500">Başarısız</span>
                    <span className="text-[10px] text-gray-400">{d.error?.slice(0, 80)}</span>
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-[10px]">Üretiliyor…</span>
                  </div>
                )}
              </div>
              {/* Aksiyonlar */}
              <div className="flex items-center justify-between border-t border-gray-100 bg-white px-2 py-1.5">
                <div className="flex min-w-0 items-center gap-1">
                  {d.sku && <span className="truncate font-mono text-[9px] font-semibold text-gray-600" title={d.sku}>{d.sku}</span>}
                  {d.parentId ? (
                    <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-medium text-indigo-700">Revize</span>
                  ) : d.fidelity === "external" ? (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">Yüklendi</span>
                  ) : (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[9px] text-gray-500">
                      {(isFidelity(d.fidelity) ? FIDELITY_LABELS[d.fidelity] : d.fidelity)}
                    </span>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  {d.status === "completed" && d.imageUrl && (
                    <>
                      <a href={imgUrl(d.imageUrl)} download target="_blank" rel="noopener noreferrer" title="İndir" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                        <Download className="h-3.5 w-3.5" />
                      </a>
                      <button onClick={() => copyDesignPrompt(d)} title="Bu tasarımın prompt'unu kopyala" className="rounded p-1 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600">
                        {copiedDesignId === d.id ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => { setRevising(revising === d.id ? null : d.id); setRevisePrompt(""); }} title="Revize et" className="rounded p-1 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  <button onClick={() => delM.mutate(d.id)} title="Sil" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {/* Revize girişi */}
              {revising === d.id && (
                <div className="border-t border-gray-100 bg-white p-2">
                  <input
                    autoFocus
                    value={revisePrompt}
                    onChange={(e) => setRevisePrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && revisePrompt.trim()) {
                        genM.mutate({ prompt: revisePrompt.trim(), model, fidelity: "close", referenceDesignId: d.id });
                      }
                    }}
                    placeholder="Revize talimatı (örn. kartalı büyüt)…"
                    className="w-full rounded border border-gray-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-200"
                  />
                  <button
                    onClick={() => revisePrompt.trim() && genM.mutate({ prompt: revisePrompt.trim(), model, fidelity: "close", referenceDesignId: d.id })}
                    disabled={!revisePrompt.trim() || genM.isPending}
                    className="mt-1 w-full rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Revize Et
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {detail && (
        <DesignDetailModal
          design={detail}
          showOriginal={showOriginal}
          onToggleOriginal={() => setShowOriginal((v) => !v)}
          onClose={() => { setDetail(null); void queryClient.invalidateQueries({ queryKey: ["analysis-designs", analysis.id] }); }}
          onDelete={() => { setDetail(null); void queryClient.invalidateQueries({ queryKey: ["analysis-designs", analysis.id] }); }}
          onRevert={() => setDetail(null)}
          onOpenEditor={() => window.open(`/designs?edit=${detail.id}`, "_blank")}
          onLinkAnalysis={() => window.open(`/designs`, "_blank")}
          onRemoveAnalysis={() => { /* analiz sayfasinda baglanti kaldirilmaz */ }}
          onSellingMockupChange={() => { /* satan mockup tasarim modalinda */ }}
          onUpdate={async (payload) => {
            await apiClient.updateDesign(detail.id, payload);
            setDetail(await apiClient.getDesign(detail.id));
          }}
          onRefresh={async () => { setDetail(await apiClient.getDesign(detail.id)); }}
        />
      )}
    </section>
  );
}
